import {
  useBasketEvents,
  useCheckoutEvents,
  useContentEvents,
  useCustomerEvents,
  useFeatureEvents,
  useFilterEvents,
  useNewsletterEvents,
  useProductEvents,
  usePurchaseEvents,
  useSearchEvents,
  useShopEvents,
  useUserActionEvents,
  useWishlistEvents,
  useLoginErrorEvent,
} from './tracking'
import { useTracking } from '~/composables/useTracking'
import { mapProductToTrackingPayload, mapTrackingDataForEvent } from '~/utils'
import type { TrackingEvent, TrackingPayload } from '~/types/tracking'
import { TRACKING_CONFIG } from '~/config/tracking'

const ALLOWED_EVENTS = [
  'page_view',
  'add_to_cart',
  'view_item',
  'view_item_list',
  'cart_summary',
  'purchase',
  'change_shop',
  'product_size_selection',
  'select_item',
  'dataLayerInit',
  'begin_checkout',
  'customer_data',
  'newsletter_sign_up',
  'newsletter_doi',
  'back_in_stock',
  'add_to_wishlist',
  'remove_from_wishlist',
  'login_error',
  'login',
]

const IMMEDIATE_EVENTS = ['dataLayerInit', 'select_item']

/**
 * Higher order composable which serves as a single entry point to unify different ecommerce tracking events
 */
export function useTrackingEvents() {
  const { push } = useTracking()

  const track = (event: TrackingEvent, payload: TrackingPayload) => {
    if (import.meta.server) {
      return
    }

    // Check if event is disabled in configuration
    if (TRACKING_CONFIG.disabledEvents.includes(event as any)) {
      if (TRACKING_CONFIG.debugDisabledEvents) {
        console.log(`[Tracking] Event '${event}' is disabled by configuration`)
      }
      return
    }

    const eventData: Partial<{
      event: TrackingEvent
      ecommerce?: TrackingPayload
    }> = mapTrackingDataForEvent(event, payload)
    if (eventData.event && ALLOWED_EVENTS.includes(eventData.event)) {
      return push(eventData, IMMEDIATE_EVENTS.includes(eventData.event))
    }
  }

  /**
   * NOTE: Important considerations:
   * sub-composable(s) such as useBasketEvents, etc. should not be utilized directly
   * instead make use of the higher order composable (useTrackingEvents) to keep things clean
   * new events should be added to relevant sub-composable to ensure DRY code and separation of concerns
   */

  return {
    ...useShopEvents(track),
    ...useBasketEvents(track),
    ...useWishlistEvents(track),
    ...useProductEvents(track),
    ...useCheckoutEvents(track),
    ...useSearchEvents(track),
    ...usePurchaseEvents(),
    ...useFilterEvents(track),
    ...useUserActionEvents(track),
    ...useCustomerEvents(track),
    ...useContentEvents(track),
    ...useFeatureEvents(track),
    ...useNewsletterEvents(track),
    ...useLoginErrorEvent(track),
    mapProductToTrackingPayload,
  }
}
